import type { SceneProject } from "@/types/scene";

export const PROJECT_STORAGE_KEY = "3d-editor-project-local-project";

const defaultCamera = { position: { x: 8, y: 6, z: 10 }, target: { x: 0, y: 0, z: 0 }, fieldOfView: 48 };
const defaultEnvironment = { backgroundColor: "#252d30", showGrid: true, groundEnabled: true, ambientIntensity: 1.15 };

export function createProject(name: string, objects: SceneProject["objects"], id = "local-project", timeline: SceneProject["timeline"] = [], duration = 15): SceneProject {
  const serializableObjects = objects.map((object) => ({ ...object, modelUrl: object.modelUrl.startsWith("blob:") ? "" : object.modelUrl }));
  return { id, name, duration, objects: serializableObjects, timeline, camera: defaultCamera, environment: defaultEnvironment };
}

export function serializeProject(project: SceneProject): string {
  return JSON.stringify(project, null, 2);
}

export function parseProject(input: string): SceneProject {
  const value: unknown = JSON.parse(input);
  if (!value || typeof value !== "object") throw new Error("Project file is not valid JSON.");
  const project = value as Partial<SceneProject>;
  if (typeof project.id !== "string" || typeof project.name !== "string" || !Array.isArray(project.objects)) throw new Error("Project file is missing required fields.");
  for (const object of project.objects) {
    if (!object || typeof object.id !== "string" || typeof object.assetId !== "string" || !object.transform) throw new Error("Project contains an invalid scene object.");
  }
  if (project.timeline !== undefined && !Array.isArray(project.timeline)) throw new Error("Project timeline is invalid.");
  project.timeline ??= [];
  project.duration = typeof project.duration === "number" && project.duration > 0 ? project.duration : 15;
  return { ...createProject(project.name, project.objects, project.id), ...project } as SceneProject;
}

export function saveLocalProject(project: SceneProject): void {
  localStorage.setItem(PROJECT_STORAGE_KEY, serializeProject(project));
}

export function loadLocalProject(): SceneProject | null {
  const value = localStorage.getItem(PROJECT_STORAGE_KEY);
  return value ? parseProject(value) : null;
}

export function downloadProject(project: SceneProject): void {
  const blob = new Blob([serializeProject(project)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${project.name.trim().replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase() || "scene"}.json`;
  link.click();
  URL.revokeObjectURL(url);
}
